// АНИМ.2: клиентский плеер боевых событий PvP.
//
// Бутафория: финальный state применяется СРАЗУ через обычный
// applyDirect (см. BattleScreen). Этот плеер только проигрывает
// overlay-события поверх. НЕ мутирует prevMatch. НЕ блокирует
// state-update'ы. Если событие потерялось — overlay просто не
// нарисуется, игра продолжится.
//
// API: playCombatEvents(rawStrings, animator) — async, никогда не
// throws, никогда не висит дольше суммы timing'ов всех событий.

/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Animator, OverlayEvent, RectInfo, CardSnapshot } from '../animation/Animator';
import { T } from '../animation/Animator';

// Зеркало CombatEvent из server/src/colyseus/anim-recorder.ts.
type ServerCardSnap = {
  instanceId: number;
  id: string;
  name_ru: string;
  faction: string;
  atk: number; armor: number; hp: number; maxHp: number;
  isHero: boolean;
  ownerIdx: 0 | 1;
};
type ServerEvent =
  | { type: 'reveal'; instanceId: number; ownerIdx: 0 | 1 }
  | { type: 'attack';
      attacker: ServerCardSnap;
      target: ServerCardSnap;
      atkDmg: number;
      defDmg: number;
    }
  | { type: 'death'; instanceId: number; ownerIdx: 0 | 1;
      name_ru: string; faction: string; atk: number }
  | { type: 'hero_death'; ownerIdx: 0 | 1 }
  | { type: 'hero_immune'; ownerIdx: 0 | 1 }
  | { type: 'stealth_activate'; instanceId: number; ownerIdx: 0 | 1 }
  | { type: 'play_action';
      cardId: string;
      cardName: string;
      faction: string;
      targetInstanceId: number;
      targetOwnerIdx: 0 | 1;
      targetIsHero: boolean;
    }
  | { type: 'heal'; instanceId: number; ownerIdx: 0 | 1; amount: number;
      targetIsHero: boolean }
  | { type: 'buff'; instanceId: number; ownerIdx: 0 | 1;
      dAtk: number; dArmor: number; dHp: number;
      targetIsHero: boolean }
  | { type: 'summon'; instanceId: number; ownerIdx: 0 | 1; line: number; slot: number }
  | { type: 'redirect';
      fromInstanceId: number;
      toInstanceId: number;
      ownerIdx: 0 | 1;
      reason: 'sekretar' | 'taunt';
    }
  | { type: 'damage';
      instanceId: number;
      ownerIdx: 0 | 1;
      amount: number;
      targetIsHero: boolean;
    }
  | { type: 'parachute'; ownerIdx: 0 | 1 }
  | { type: 'raschet';
      fromInstanceId: number;
      fromOwnerIdx: 0 | 1;
      toInstanceId: number;
      toOwnerIdx: 0 | 1;
      toIsHero: boolean;
    }
  | { type: 'move';
      instanceId: number;
      ownerIdx: 0 | 1;
      name_ru: string;
      faction: string;
      atk: number;
      fromLine: number;
      fromSlot: number;
      toLine: number;
      toSlot: number;
    }
  | { type: 'influence';
      cardId: string;
      cardName: string;
      targetInstanceId: number;
      targetOwnerIdx: 0 | 1;
      targetIsHero: boolean;
    }
  | { type: 'stack_up';
      instanceId: number;
      ownerIdx: 0 | 1;
      stackKind: 'mod' | 'infected';
      count: number;
    }
  | { type: 'keyword_flash';
      instanceId: number;
      ownerIdx: 0 | 1;
      keyword: string;
      mode: 'add' | 'remove';
    }
  | { type: 'phase_banner';
      phase: number;
      label: string;
    }
  | { type: 'card_draw';
      ownerIdx: 0 | 1;
      faction: string;
    }
  | { type: 'card_placement';
      instanceId: number;
      ownerIdx: 0 | 1;
      line: number;
      slot: number;
      name_ru: string;
      faction: string;
      atk: number;
    }
  | { type: 'pause' };

function parseEvents(raw: string[]): ServerEvent[] {
  const out: ServerEvent[] = [];
  for (const s of raw) {
    try { out.push(JSON.parse(s)); } catch {}
  }
  return out;
}

// Wrapper над Animator.getRect (DOM → rectCache fallback).
function findRect(instanceId: number, animator: Animator): RectInfo | null {
  return animator.getRect(instanceId);
}

// АНИМ.5-fix-hero-id: Hero на сервере имеет instanceId=0 (engine не
// назначает), а на клиенте synthetic -1 (мой) / -2 (вражеский). Для
// атаки/смерти Hero используем synthetic.
function rectForServerSnap(s: ServerCardSnap, animator: Animator): RectInfo | null {
  if (s.isHero) {
    const heroId = s.ownerIdx === 0 ? -1 : -2;
    return animator.getRect(heroId);
  }
  return animator.getRect(s.instanceId);
}

function snapToOverlay(s: ServerCardSnap): CardSnapshot {
  return {
    instanceId: s.instanceId,
    id: s.id,
    name_ru: s.name_ru,
    faction: s.faction,
    atk: s.atk, armor: s.armor, hp: s.hp, maxHp: s.maxHp,
    isHero: s.isHero,
  };
}

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ОТЛ: счётчик последнего батча для debug-баннера.
function recordBatch(status: 'start' | 'done', count: number, tick?: number) {
  try {
    (window as any).__lastCombatBatch__ = {
      status, count,
      tick: tick ?? (window as any).__lastCombatBatch__?.tick ?? 0,
      ts: Date.now(),
    };
  } catch {}
}

// Главная функция. Никогда не throws, никогда не висит вечно.
// Rect находится через animator.getRect (DOM → rectCache fallback).
// rectCache обновляется BattleScreen useEffect на каждом render.
export async function playCombatEvents(
  raw: string[],
  animator: Animator,
  myServerIdx: 0 | 1 = 0
): Promise<void> {
  const events = parseEvents(raw);
  console.log(`[anim-replay] start, ${events.length} events, myServerIdx=${myServerIdx}`);
  recordBatch('start', events.length);
  if (events.length === 0) { recordBatch('done', 0); return; }

  // АНИМ-fix-perspective (БУГ.26): match-adapter свапает массив players
  // так что для клиента всегда [me, enemy]. Synthetic Hero ID -1 = me,
  // -2 = enemy. НО серверные события содержат ownerIdx с server-perspective
  // (0 = first player, 1 = second). Если мой server idx === 1, то ВСЕ
  // ownerIdx в событиях надо инвертировать: server's player 0 = enemy для
  // меня (-2), player 1 = me (-1). Иначе анимация смерти/иммунитета/heal/
  // damage/buff и тп играется на ВРАЖЕСКОМ слоте вместо моего и наоборот.
  // Этот баг проявлялся как «убил врага Героем — анимация смерти у себя»
  // (для seat=1 при победе).
  if (myServerIdx === 1) {
    const flip = (x: any): 0 | 1 => (x === 0 ? 1 : 0);
    for (const e of events as any[]) {
      if (e.ownerIdx !== undefined) e.ownerIdx = flip(e.ownerIdx);
      if (e.targetOwnerIdx !== undefined) e.targetOwnerIdx = flip(e.targetOwnerIdx);
      if (e.fromOwnerIdx !== undefined) e.fromOwnerIdx = flip(e.fromOwnerIdx);
      if (e.toOwnerIdx !== undefined) e.toOwnerIdx = flip(e.toOwnerIdx);
      // attack-event: ownerIdx у attacker/target snap'ов.
      if (e.attacker?.ownerIdx !== undefined) e.attacker.ownerIdx = flip(e.attacker.ownerIdx);
      if (e.target?.ownerIdx !== undefined) e.target.ownerIdx = flip(e.target.ownerIdx);
    }
  }

  // Принудительно сбрасываем стейл-event если он был.
  if (animator.current) {
    animator.current = null;
  }

  for (let i = 0; i < events.length; i++) {
    if (animator.shouldStop()) {
      console.log('[anim-replay] shouldStop at i=' + i);
      break;
    }
    const e = events[i];
    try {
      switch (e.type) {
        case 'reveal': {
          const rect = findRect(e.instanceId, animator);
          if (rect) {
            const ev: OverlayEvent = { kind: 'reveal', instanceId: e.instanceId, rect };
            animator.current = ev;
            animator.notify();
          }
          await wait(T.reveal);
          if (animator.current?.kind === 'reveal' && animator.current.instanceId === e.instanceId) {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'attack': {
          const aRect = rectForServerSnap(e.attacker, animator);
          const tRect = rectForServerSnap(e.target, animator);
          if (!aRect || !tRect) {
            // DOM не нашли — пропускаем без overlay, но даём паузу.
            await wait(T.attackTotal / 2);
            break;
          }
          // Stage 1: telegraph + lunge (без damage numbers)
          animator.current = {
            kind: 'attack',
            attacker: snapToOverlay(e.attacker),
            target: snapToOverlay(e.target),
            attackerRect: aRect, targetRect: tRect,
            stage: 'telegraph',
            atkDmg: null, defDmg: null,
          };
          animator.notify();
          await wait(T.telegraph);
          if (animator.shouldStop()) break;
          if (animator.current?.kind === 'attack') {
            animator.current.stage = 'lunge';
            animator.notify();
          }
          await wait(T.lunge);
          if (animator.shouldStop()) break;
          // Stage 2: impact (damage numbers появляются)
          if (animator.current?.kind === 'attack') {
            animator.current.stage = 'impact';
            animator.current.atkDmg = e.atkDmg;
            animator.current.defDmg = e.defDmg;
            animator.notify();
          }
          await wait(T.impact);
          if (animator.shouldStop()) break;
          // Stage 3: return
          if (animator.current?.kind === 'attack') {
            animator.current.stage = 'return';
            animator.notify();
          }
          await wait(T.returnMs);
          if (animator.current?.kind === 'attack') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'death': {
          const rect = findRect(e.instanceId, animator);
          if (rect) {
            // АНИМ.17: discard fly — ищем discard pile владельца юнита.
            const side = e.ownerIdx === 1 ? 'enemy' : 'my';
            let discardEl = document.querySelector(`[data-discard="${side}"]`) as HTMLElement | null;
            if (!discardEl) discardEl = document.querySelector('[data-discard]') as HTMLElement | null;
            let discardRect: RectInfo | null = null;
            if (discardEl) {
              const r = discardEl.getBoundingClientRect();
              discardRect = { left: r.left, top: r.top, width: r.width, height: r.height };
            }
            const snap: CardSnapshot = {
              instanceId: e.instanceId,
              id: '', name_ru: e.name_ru, faction: e.faction,
              atk: e.atk, armor: 0, hp: 0, maxHp: 0, isHero: false,
            };
            animator.current = {
              kind: 'death', instanceId: e.instanceId, rect, discardRect, snap,
            };
            animator.notify();
          }
          await wait(T.death);
          if (animator.current?.kind === 'death' && animator.current.instanceId === e.instanceId) {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'hero_death': {
          // Hero rect — ищем по data-instance-id (-1 мой, -2 враг).
          const heroId = e.ownerIdx === 0 ? -1 : -2;
          const rect = findRect(heroId, animator);
          if (rect) {
            animator.current = { kind: 'heroDeath', ownerIdx: e.ownerIdx, rect };
            animator.notify();
          }
          await wait(T.heroDeath);
          if (animator.current?.kind === 'heroDeath') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'pause': {
          await wait(T.pause);
          break;
        }
        case 'hero_immune': {
          const heroId = e.ownerIdx === 0 ? -1 : -2;
          const rect = findRect(heroId, animator);
          if (rect) {
            animator.current = { kind: 'heroImmune', ownerIdx: e.ownerIdx, rect };
            animator.notify();
          }
          await wait(T.immune);
          if (animator.current?.kind === 'heroImmune') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'stealth_activate': {
          const rect = findRect(e.instanceId, animator);
          if (rect) {
            animator.current = { kind: 'stealthActivate', instanceId: e.instanceId, rect };
            animator.notify();
          }
          await wait(T.stealth);
          if (animator.current?.kind === 'stealthActivate') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'play_action': {
          let targetRect: RectInfo | null = null;
          if (e.targetInstanceId) {
            if (e.targetIsHero) {
              const heroId = e.targetOwnerIdx === 0 ? -1 : -2;
              targetRect = findRect(heroId, animator);
            } else {
              targetRect = findRect(e.targetInstanceId, animator);
            }
          }
          animator.current = {
            kind: 'playAction',
            cardId: e.cardId,
            cardName: e.cardName,
            faction: e.faction,
            targetRect,
          };
          animator.notify();
          await wait(T.playAction);
          if (animator.current?.kind === 'playAction') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'heal': {
          // АНИМ.12: Hero ищем через synthetic -1/-2 (нет instanceId).
          let rect: RectInfo | null = null;
          if (e.targetIsHero) {
            const heroId = e.ownerIdx === 0 ? -1 : -2;
            rect = findRect(heroId, animator);
          } else {
            rect = findRect(e.instanceId, animator);
          }
          if (rect) {
            animator.current = { kind: 'heal', instanceId: e.instanceId, rect, amount: e.amount };
            animator.notify();
          }
          await wait(T.heal);
          if (animator.current?.kind === 'heal') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'buff': {
          // АНИМ.12: Hero ищем через synthetic -1/-2.
          let rect: RectInfo | null = null;
          if (e.targetIsHero) {
            const heroId = e.ownerIdx === 0 ? -1 : -2;
            rect = findRect(heroId, animator);
          } else {
            rect = findRect(e.instanceId, animator);
          }
          if (rect) {
            animator.current = {
              kind: 'buff', instanceId: e.instanceId, rect,
              dAtk: e.dAtk, dArmor: e.dArmor, dHp: e.dHp,
            };
            animator.notify();
          }
          await wait(T.buff);
          if (animator.current?.kind === 'buff') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'summon': {
          const rect = findRect(e.instanceId, animator);
          if (rect) {
            animator.current = { kind: 'summon', instanceId: e.instanceId, rect };
            animator.notify();
          }
          await wait(T.summon);
          if (animator.current?.kind === 'summon') {
            animator.current = null;
            animator.notify();
          }
          // АНИМ.7-fix-cache: дать React время отрисовать нового юнита,
          // потом обновить rect-кэш. Чтобы если следующий event — death
          // этого же юнита (Развёртывание + Расчёт убил один сразу) —
          // findRect нашёл его в DOM или в обновлённом кэше.
          await new Promise<void>((r) => requestAnimationFrame(() => r()));
          animator.updateRectCache();
          break;
        }
        case 'redirect': {
          const fromRect = findRect(e.fromInstanceId, animator);
          const toRect = findRect(e.toInstanceId, animator);
          if (fromRect && toRect) {
            animator.current = { kind: 'redirect', fromRect, toRect, reason: e.reason };
            animator.notify();
          }
          await wait(T.redirectArrow);
          if (animator.current?.kind === 'redirect') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'damage': {
          let rect: RectInfo | null = null;
          if (e.targetIsHero) {
            const heroId = e.ownerIdx === 0 ? -1 : -2;
            rect = findRect(heroId, animator);
          } else {
            rect = findRect(e.instanceId, animator);
          }
          if (rect) {
            animator.current = {
              kind: 'damage',
              instanceId: e.instanceId,
              rect,
              amount: e.amount,
              isHero: e.targetIsHero,
            };
            animator.notify();
          }
          await wait(500);
          if (animator.current?.kind === 'damage') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'parachute': {
          const heroId = e.ownerIdx === 0 ? -1 : -2;
          const rect = findRect(heroId, animator);
          if (rect) {
            animator.current = { kind: 'parachute', ownerIdx: e.ownerIdx, rect };
            animator.notify();
          }
          await wait(T.parachute);
          if (animator.current?.kind === 'parachute') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'raschet': {
          // АНИМ.9: Расчёт стреляет ДО основного боя. From — сам Расчёт,
          // To — attacker (юнит или Hero), который получает прерывающий
          // выстрел.
          const fromRect = findRect(e.fromInstanceId, animator);
          let toRect: RectInfo | null = null;
          if (e.toIsHero) {
            const heroId = e.toOwnerIdx === 0 ? -1 : -2;
            toRect = findRect(heroId, animator);
          } else {
            toRect = findRect(e.toInstanceId, animator);
          }
          if (fromRect && toRect) {
            animator.current = {
              kind: 'raschet', fromRect, toRect, toIsHero: e.toIsHero,
            };
            animator.notify();
          }
          await wait(T.raschet);
          if (animator.current?.kind === 'raschet') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'move': {
          // АНИМ.11: перемещение юнита. fromRect ищем по data-side/line/slot,
          // toRect — по data-instance-id (юнит уже в новой ячейке после
          // mutation) ИЛИ по data-side/line/slot нового слота.
          // ownerIdx 0/1 → data-side "my"/"enemy". В PvP с точки зрения
          // клиента "my" — это его собственный idx (myIdxRef). Здесь
          // используем абсолютные 0/1 — клиент должен сам определить.
          // Hack: ownerIdx — это owner юнита в engine state, для текущего
          // клиента "my" соответствует ownerIdx === g.idx. Чтобы избежать
          // зависимости от g.idx здесь, проверяем data-side и для my, и
          // для enemy.
          const tryRectOfSlot = (line: number, slot: number): RectInfo | null => {
            for (const side of ['my', 'enemy']) {
              const el = document.querySelector(
                `[data-side="${side}"][data-line="${line}"][data-slot="${slot}"]`
              ) as HTMLElement | null;
              // Проверяем что в этой ячейке находится наш юнит или
              // пустая (для toRect). Для fromRect — пустая (юнит ушёл).
              if (el) {
                const r = el.getBoundingClientRect();
                return { left: r.left, top: r.top, width: r.width, height: r.height };
              }
            }
            return null;
          };
          // toRect — приоритет на актуальный DOM юнита (он уже в новой ячейке).
          let toRect: RectInfo | null = animator.getRect(e.instanceId);
          if (!toRect) toRect = tryRectOfSlot(e.toLine, e.toSlot);
          // fromRect — там, где юнита уже нет, ищем по data-side+line+slot.
          // Если ячейка содержит юнита (DOM ещё не успел обновиться) — это
          // прежний rect юнита, тоже подходит.
          const fromRect = tryRectOfSlot(e.fromLine, e.fromSlot);
          if (fromRect && toRect) {
            animator.current = {
              kind: 'move',
              snap: {
                instanceId: e.instanceId,
                id: '', name_ru: e.name_ru, faction: e.faction,
                atk: e.atk, armor: 0, hp: 0, maxHp: 0,
                isHero: false,
              },
              fromRect, toRect,
            };
            animator.notify();
          }
          await wait(T.move);
          if (animator.current?.kind === 'move') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'influence': {
          let targetRect: RectInfo | null = null;
          if (e.targetInstanceId) {
            if (e.targetIsHero) {
              const heroId = e.targetOwnerIdx === 0 ? -1 : -2;
              targetRect = findRect(heroId, animator);
            } else {
              targetRect = findRect(e.targetInstanceId, animator);
            }
          }
          animator.current = {
            kind: 'influence',
            cardId: e.cardId,
            cardName: e.cardName,
            targetRect,
          };
          animator.notify();
          await wait(T.influence);
          if (animator.current?.kind === 'influence') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'stack_up': {
          const rect = findRect(e.instanceId, animator);
          if (rect) {
            animator.current = {
              kind: 'stackUp',
              instanceId: e.instanceId,
              rect,
              stackKind: e.stackKind,
              count: e.count,
            };
            animator.notify();
          }
          await wait(T.stackUp);
          if (animator.current?.kind === 'stackUp') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'keyword_flash': {
          const rect = findRect(e.instanceId, animator);
          if (rect) {
            animator.current = {
              kind: 'keywordFlash',
              instanceId: e.instanceId,
              rect,
              keyword: e.keyword,
              mode: e.mode,
            };
            animator.notify();
          }
          await wait(T.keywordFlash);
          if (animator.current?.kind === 'keywordFlash') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'phase_banner': {
          animator.current = {
            kind: 'phaseBanner',
            phase: e.phase,
            label: e.label,
          };
          animator.notify();
          await wait(T.phaseBanner);
          if (animator.current?.kind === 'phaseBanner') {
            animator.current = null;
            animator.notify();
          }
          break;
        }
        case 'card_draw': {
          // АНИМ.16: используем Animator.cardDraw (он сам ищет rect).
          await animator.cardDraw(e.ownerIdx, e.faction);
          break;
        }
        case 'card_placement': {
          // АНИМ.16: используем Animator.cardPlacement. Передаём фейковый
          // unit-объект с нужными полями.
          const fakeUnit = {
            instanceId: e.instanceId,
            ownerId: e.ownerIdx,
            name_ru: e.name_ru,
            faction: e.faction,
            atk: e.atk,
            type: 'unit',
            base: { atk: e.atk, armor: 0, hp: 0 },
            current: { atk: e.atk, armor: 0, hp: 0 },
          };
          await animator.cardPlacement(fakeUnit, e.line, e.slot, e.ownerIdx);
          break;
        }
      }
    } catch (err) {
      console.warn(`[anim-replay] event[${i}] type=${(e as any).type} crash:`, err);
    }
  }

  // Финальный cleanup на всякий случай.
  if (animator.current) {
    animator.current = null;
    animator.notify();
  }

  console.log('[anim-replay] done');
  recordBatch('done', events.length);
}
